// Roadmap 2.2 — every rule the app applies, in one spreadsheet, for review
// away from the screen (backend: GET /settings/rules/export.xlsx).
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DownloadSimple } from "@phosphor-icons/react";
import { downloadRulesWorkbook } from "../lib/api/downloads";
import { getRulesWatchSummary } from "../lib/authApi";

export const RulesDownloadPanel = ({ isAdmin }) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [watch, setWatch] = useState(null);
  useEffect(() => {
    if (!isAdmin) return;
    getRulesWatchSummary().then(setWatch).catch(() => setWatch(null));
  }, [isAdmin]);
  if (!isAdmin) return null;
  const onClick = async () => {
    setBusy(true);
    setError("");
    try {
      await downloadRulesWorkbook();
    } catch {
      setError(t("ui.settings.rules_download.failed",
        "The download failed. Try again; if it keeps failing, the server log says why."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="rules-download" className="mt-4 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
      <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-50">
        {t("ui.settings.rules_download.title", "All rules")}
      </h4>
      <p className="text-xs text-slate-600 dark:text-slate-300 mt-1 max-w-prose">
        {t("ui.settings.rules_download.body",
          "Every rule the app applies — deduction of input VAT per expense type, the reverse-charge and use-and-enjoyment elections per country, and each return form's editions — with the dates the law applies, the official source and when it was last reviewed. Fill the REVIEW columns and send the file back: each change becomes a new version of the rule.")}
      </p>
      <button
        type="button"
        data-testid="rules-download-button"
        onClick={onClick}
        disabled={busy}
        className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-teal-700 dark:text-teal-300 bg-teal-600/10 hover:bg-teal-600/20 border border-teal-400/50 px-3 py-1.5 rounded-xl disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-teal-400"
      >
        <DownloadSimple size={13} weight="bold" aria-hidden />
        {busy ? t("ui.settings.rules_download.busy", "Preparing…")
              : t("ui.settings.rules_download.button", "Download all rules (XLSX)")}
      </button>
      {watch && (
        <p data-testid="rules-watch-summary"
           className={`text-xs mt-2 tabular-nums ${watch.due_now ? "text-amber-800 dark:text-amber-200" : "text-slate-600 dark:text-slate-300"}`}>
          {t("ui.settings.rules_download.watch", {
            due: watch.due_now, assess: watch.to_assess,
            defaultValue: "Law watch: {{due}} official source(s) due a re-check now; {{assess}} announced change(s) still to assess. Both are listed on the Watch list sheet.",
          })}
        </p>
      )}
      {error && <p role="alert" className="text-xs text-red-700 dark:text-red-300 mt-2">{error}</p>}
    </div>
  );
};
